import { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { RootStore, RootStoreContext } from '../../stores/rootStore';
import { updatePaymentMethodPost } from '../../api/orderService';

//sessionStorage key: which attention state the customer dismissed (kind + subscription id);
//a different problem, or the same one in a new session, shows the bar again. The account
//menu badge ignores the dismissal on purpose.
export const ATTENTION_DISMISSED_KEY = 'dcr.attentionDismissed';

const fmtDate = (s: string | undefined | null) => {
    if (!s) return '';
    const d = new Date(s);
    return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const readDismissed = (): string | null => {
    try { return sessionStorage.getItem(ATTENTION_DISMISSED_KEY); } catch { return null; }
};

//Site-wide notice for a subscription that needs the customer's action (design D14): shown
//under the nav on every page while signed in, so a failed renewal or a lapsed licence is
//seen without opening the subscriptions page.
const AttentionBar = observer(() => {
    const { subscriptionsStore, userStore }: RootStore = useContext(RootStoreContext);
    const attention = subscriptionsStore.attention;
    const [dismissed, setDismissed] = useState<string | null>(readDismissed);
    const [updatingCard, setUpdatingCard] = useState(false);
    const [cardError, setCardError] = useState('');
    //the shared NavBar is position:fixed, so the bar is fixed too and sits right under it
    //(the pages leave 120px of top padding, which the nav + bar fit in)
    const [top, setTop] = useState(64);
    useEffect(() => {
        const measure = () => {
            const nav = document.querySelector('.dcr-nav');
            if (nav) setTop(Math.round(nav.getBoundingClientRect().height));
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    if (!userStore.isLoggedIn || !attention) return null;

    const token = `${attention.kind}:${attention.subscription.id}`;
    if (dismissed === token) return null;

    const dismiss = () => {
        try { sessionStorage.setItem(ATTENTION_DISMISSED_KEY, token); } catch { }
        setDismissed(token);
    };

    //never a silent no-op: a failed portal call (Stripe unreachable, no portal configuration,
    //or a test customer) is said in the bar, like the renew page does
    const handleUpdateCard = async () => {
        setUpdatingCard(true);
        setCardError('');
        try {
            const url = await updatePaymentMethodPost(attention.subscription.id);
            if (url) { window.location.href = url; return; }
            setCardError('Payment details cannot be updated right now. Please try again later.');
        } catch (e) {
            console.error('Failed to open the card-update page', e);
            setCardError('Payment details cannot be updated right now. Please try again later.');
        }
        setUpdatingCard(false);
    };

    const sub = attention.subscription;
    let className = 'attention-bar';
    let text: string;
    let actions: JSX.Element;

    if (attention.kind === 'pastDue') {
        className += ' danger';
        text = 'The renewal payment for your Pro subscription didn’t go through. Pay the open invoice or update your card — Pro continues once the payment succeeds.';
        actions = (
            <>
                {sub.hostedInvoiceUrl && <a href={sub.hostedInvoiceUrl} className="attention-cta">Pay now</a>}
                {sub.canUpdatePaymentMethod && (
                    <button type="button" className="attention-cta" disabled={updatingCard} onClick={handleUpdateCard}>
                        {updatingCard ? 'Opening…' : 'Update payment details'}
                    </button>
                )}
                <a href="/subscriptions" className="attention-link">Details</a>
                {cardError && <span className="attention-error">{cardError}</span>}
            </>
        );
    } else if (attention.kind === 'expired') {
        className += ' danger';
        text = `Your Pro subscription expired on ${fmtDate(sub.endDate)}. Renew to keep recording without limits.`;
        actions = (
            <>
                {sub.renewalLink && <a href={sub.renewalLink} className="attention-cta">Renew</a>}
                <a href="/subscriptions" className="attention-link">Details</a>
            </>
        );
    } else {
        className += ' warn';
        const when = attention.daysLeft <= 0 ? 'today' : attention.daysLeft === 1 ? 'tomorrow' : `on ${fmtDate(sub.endDate)}`;
        text = `Your Pro subscription expires ${when} and auto-renewal is off.`;
        actions = (
            <>
                {sub.renewalLink && <a href={sub.renewalLink} className="attention-cta">Renew</a>}
                <a href="/subscriptions" className="attention-link">Details</a>
            </>
        );
    }

    return (
        <div className={className} role="status" style={{ top }}>
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
            <span className="attention-text">{text}</span>
            <span className="attention-actions">{actions}</span>
            <button type="button" className="attention-close" onClick={dismiss} aria-label="Dismiss">{'×'}</button>
        </div>
    );
});

export default AttentionBar;
